import React, { ComponentProps } from 'react';

// Components
import { MainNavigationGroup } from './partials/MainNavigationGroup/MainNavigationGroup';
import { MainNavigationTopItem } from './partials/MainNavigationTopItem/MainNavigationTopItem';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { BlockGuard } from '../../types/api/Block';
import { BlockType } from '../../types/api/ElementType';
import { asBlock } from '../../_lib/BlockFetcher';
import { useSonTranslation } from '../../translation/useSonTranslation';

import { useMainNavigation } from './useMainNavigation';
import { SubPanel } from './partials/SubPanels/SubPanel';
import { MainNavigationProps } from './MainNavigationTypes';

function MainNavigation(props: MainNavigationProps) {
  const { t } = useSonTranslation('MainNavigation');

  const {
    navRef,
    subPanelsContainerRef,
    activeOpenButtonId,
    subPanelContainerClass,
    subPanelsSlideClass,
    setActiveOpenButtonId,
    closeMainNavigationWhenFocusOut,
    closeAllSubPanels,
    closeSubPanelsOnEscape,
    fetchSubPanel,
    closeSubPanel,
    keysOfOpenPanels,
    mapRef,
  } = useMainNavigation(props);

  return (
    // we add a (redundant) role "navigation" for better screen reader support
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <nav
      ref={navRef}
      className={keysOfOpenPanels.length > 1 ? 'main-navigation main-navigation--sub-panel-visible' : 'main-navigation'}
      id={GlobalDocumentIds.mainNavigation}
      aria-label={t('headline')}
      onBlur={closeMainNavigationWhenFocusOut}
      onKeyDown={closeSubPanelsOnEscape}
    >
      <div className="main-navigation__panel" id={GlobalDocumentIds.mainNavigationPanel}>
        <MainNavigationTopItem
          previous={props.parent && props.current ? props.parent.navItem : undefined}
          current={props.current}
          key={props.current.navItem.link.url}
          root={props.root}
        />
        <div className="main-navigation__scroll-container">
          {props.groups?.map(mainNavigationGroup => (
            <MainNavigationGroup
              key={mainNavigationGroup.groupLabel}
              groupLabel={mainNavigationGroup.groupLabel}
              navItems={mainNavigationGroup.navItems}
              fetchSubPanel={fetchSubPanel}
              panelLevel={1}
              activeOpenButtonId={activeOpenButtonId}
              setActiveOpenButtonId={setActiveOpenButtonId}
            />
          ))}
        </div>
      </div>
      <div
        className={`${subPanelContainerClass}${subPanelsSlideClass ? ` ${subPanelsSlideClass}` : ''}`}
        ref={subPanelsContainerRef}
        id={GlobalDocumentIds.mainNavigationSubPanelContainer}
      >
        {/* all panels are in the DOM, maximum 2 are visible, transition reasons */}
        {/* future improvement: maximum 2 are in DOM */}
        {keysOfOpenPanels.slice(1, keysOfOpenPanels.length).map((key, index: number) => {
          // first element contains main navigation
          const panelState = mapRef.current.get(key);
          return panelState ? (
            <SubPanel
              key={panelState.panelLevel}
              classIndex={index + 1}
              subPanelState={panelState}
              closeSubPanel={closeSubPanel}
              fetchSubPanel={fetchSubPanel}
              closeAllSubPanels={closeAllSubPanels}
              panelLevel={panelState?.panelLevel}
              allPanelCount={keysOfOpenPanels.length - 1} // main nav panel is part of the state
            />
          ) : null;
        })}
      </div>
    </nav>
  );
}

export const MainNavigationBlock = asBlock(MainNavigation, BlockType.mainnavigation);
export type MainNavigationBlock = ComponentProps<typeof MainNavigationBlock>;
export const isMainNavigationBlock = BlockGuard<MainNavigationBlock>(BlockType.mainnavigation);
